import { useState } from 'react';
import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tooltip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react/offline';
import SendIcon from '@iconify-icons/tabler/send';
import PencilIcon from '@iconify-icons/tabler/pencil';
import CheckIcon from '@iconify-icons/tabler/check';
import PaperclipIcon from '@iconify-icons/tabler/paperclip';
import type {
  CaseDetail,
  CaseEvent,
  CaseInput,
} from 'common/src/api/shipment-cases';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { StatusChip } from '../../components/ui';
import { caseInputTiming } from './model';
import CaseAttachments from './CaseAttachments';

/** Evento que creó la solicitud y, si la hay, el que registró la respuesta. */
function requestEvents(detail: CaseDetail, input: CaseInput) {
  const byInput = (action: string) =>
    detail.events.find(
      (event) => event.action === action && event.payload.input_id === input.id
    );
  return {
    request: byInput('request_input'),
    response: byInput('receive_input'),
  };
}
const fileCount = (...events: (CaseEvent | undefined)[]) =>
  events.reduce((n, event) => n + (event?.files.length || 0), 0);

export default function CaseRequests({
  detail,
  identity,
  closed,
  disabled,
  onRequest,
  onReply,
  onAssign,
}: {
  detail: CaseDetail;
  identity: string;
  closed: boolean;
  disabled: boolean;
  onRequest: () => void;
  onReply: (id: number) => void;
  onAssign: (id: number) => void;
  /** Se conserva por compatibilidad; el detalle de la solicitud ya muestra sus documentos. */
  onHistory?: () => void;
}) {
  const { t, currentLanguage } = useTranslation('shipmentCases');
  const locale = currentLanguage === 'en' ? 'en-US' : 'es-CL';
  const [openId, setOpenId] = useState<number | null>(null);
  const today = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const pending = detail.inputs
    .filter((input) => !input.received_at)
    .sort((a, b) => a.due_on.localeCompare(b.due_on) || a.id - b.id);
  const received = detail.inputs
    .filter((input) => input.received_at)
    .sort((a, b) => b.received_at!.localeCompare(a.received_at!));
  const date = (value: string, long = false) =>
    Number.isFinite(Date.parse(value))
      ? new Intl.DateTimeFormat(
          locale,
          long
            ? { day: '2-digit', month: '2-digit', year: 'numeric' }
            : { day: '2-digit', month: '2-digit' }
        ).format(new Date(value))
      : '—';
  const dueDate = (iso: string) =>
    /^\d{4}-\d{2}-\d{2}/.test(iso) ? date(`${iso}T00:00:00`, true) : iso;
  function deadline(input: CaseInput) {
    const timing = caseInputTiming(input, today);
    if (timing.days === null) return input.due_on || '—';
    if (timing.days < 0)
      return t(
        timing.days === -1
          ? 'Vencida hace 1 día'
          : 'Vencida hace {{count}} días',
        { count: -timing.days }
      );
    if (timing.days === 0) return t('Vence hoy');
    return t(
      timing.days === 1 ? '1 día restante' : '{{count}} días restantes',
      { count: timing.days }
    );
  }
  const documentsLabel = (n: number) =>
    t(n === 1 ? '1 documento' : '{{count}} documentos', { count: n });
  const open = detail.inputs.find((input) => input.id === openId) || null;
  const openEvents = open ? requestEvents(detail, open) : null;
  const openTiming = open ? caseInputTiming(open, today) : null;
  return (
    <section
      className='flex flex-col gap-4'
      aria-label={t('Solicitudes de información')}
    >
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='min-w-0 flex-1 text-[13px] leading-snug'>
          <span className='font-semibold'>
            {t(
              pending.length === 0
                ? 'Sin solicitudes pendientes'
                : pending.length === 1
                  ? '1 pendiente'
                  : '{{count}} pendientes',
              { count: pending.length }
            )}
          </span>
          {received.length > 0 && (
            <span className='text-default-400'>
              {' '}
              · {received.length}{' '}
              {t(received.length === 1 ? 'recibida' : 'recibidas')}
            </span>
          )}
          {pending.length > 0 && !closed && (
            <span className='text-default-400'>
              {' '}
              · {t('El caso espera estas respuestas.')}
            </span>
          )}
        </div>
        {!closed && (
          <Button
            size='sm'
            color='primary'
            variant='light'
            className='border border-current'
            startContent={<Icon icon={SendIcon} />}
            isDisabled={disabled}
            onPress={onRequest}
          >
            {t('Solicitar información')}
          </Button>
        )}
      </div>
      {detail.inputs.length === 0 && (
        <div className='rounded-medium border border-dashed border-default-300 px-5 py-7 text-center text-sm text-default-400'>
          <p>{t('No hay solicitudes de información.')}</p>
          {!closed && (
            <p className='mt-1 text-xs leading-relaxed'>
              {t(
                'Solicita información a un usuario interno o a una entidad externa. Para solicitudes externas, asigna un encargado de seguimiento.'
              )}
            </p>
          )}
        </div>
      )}
      <div className='flex flex-col gap-2'>
        {pending.map((input) => {
          const timing = caseInputTiming(input, today);
          const overdue =
            timing.state === 'overdue' || timing.state === 'today';
          const { request } = requestEvents(detail, input);
          const files = fileCount(request);
          return (
            <article
              key={input.id}
              aria-label={`${t('Solicitud')} #${input.id}`}
              className={`grid grid-cols-[8px_minmax(0,1fr)] items-start gap-x-3 gap-y-2 rounded-medium border bg-content2/30 px-4 py-3 sm:grid-cols-[8px_minmax(0,1fr)_auto] ${overdue ? 'border-warning/50' : input.recipient_kind === 'external' ? 'border-secondary/40' : 'border-default-200'}`}
            >
              <span
                className={`mt-2 size-2 rounded-full ${overdue ? 'bg-warning' : input.recipient_kind === 'external' ? 'bg-secondary' : 'bg-default-400'}`}
              />
              <div className='min-w-0'>
                <button
                  type='button'
                  aria-label={`${t('Ver detalle')} #${input.id}`}
                  className='block w-full rounded-small text-left hover:bg-default-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus'
                  onClick={() => setOpenId(input.id)}
                >
                  <div className='flex flex-wrap items-center gap-2'>
                    <p className='break-words text-[13px] font-semibold'>
                      {input.recipient}
                    </p>
                    <Chip
                      size='sm'
                      variant='flat'
                      color={
                        input.recipient_kind === 'external'
                          ? 'secondary'
                          : 'default'
                      }
                      classNames={{ base: 'h-5', content: 'text-[10px]' }}
                    >
                      {t(
                        input.recipient_kind === 'external'
                          ? 'Externo'
                          : 'Interno'
                      )}
                    </Chip>
                    {input.external_entity && (
                      <span className='text-[11px] text-default-400'>
                        {input.external_entity.type}
                      </span>
                    )}
                  </div>
                  <p className='mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-default-500'>
                    {input.requested}
                  </p>
                  <p className='mt-1 text-[11px] leading-relaxed text-default-400'>
                    {t('Solicitado')} {date(input.requested_at)}
                    {request?.actor_name ? ` · ${request.actor_name}` : ''}
                    {request?.payload.details.due_days
                      ? ` · ${t('Plazo')}: ${request.payload.details.due_days} ${t('días')}`
                      : ''}
                    {files > 0 && (
                      <span className='ml-1 inline-flex items-center gap-1'>
                        · <Icon icon={PaperclipIcon} width={11} />
                        {documentsLabel(files)}
                      </span>
                    )}
                  </p>
                </button>
                {input.recipient_kind === 'external' && (
                  <div className='mt-1 flex flex-wrap items-center gap-1 text-[11px] text-default-400'>
                    <span>
                      {t('Seguimiento')}:{' '}
                      {input.followup_owner?.name || t('Sin asignar')}
                    </span>
                    {!closed && (
                      <Tooltip content={t('Editar encargado de seguimiento')}>
                        <Button
                          isIconOnly
                          size='sm'
                          variant='light'
                          className='size-6 min-w-6'
                          aria-label={`${t('Editar encargado de seguimiento')} #${input.id}`}
                          isDisabled={disabled}
                          onPress={() => onAssign(input.id)}
                        >
                          <Icon icon={PencilIcon} width={12} />
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
              <div className='col-start-2 flex flex-wrap items-center gap-3 sm:col-start-auto sm:flex-col sm:items-end sm:gap-2'>
                <span
                  className={`text-xs font-medium ${overdue ? 'text-warning' : 'text-default-400'}`}
                >
                  {deadline(input)}
                </span>
                {!closed && (
                  <Button
                    size='sm'
                    variant='flat'
                    radius='full'
                    aria-label={`${t('Registrar respuesta')} #${input.id}`}
                    isDisabled={disabled}
                    onPress={() => onReply(input.id)}
                  >
                    {t('Registrar')}
                  </Button>
                )}
              </div>
            </article>
          );
        })}
      </div>
      {received.length > 0 && (
        <section
          className='flex flex-col gap-3'
          aria-label={t('Solicitudes recibidas')}
        >
          <p className='text-[11px] font-semibold uppercase tracking-wide text-default-400'>
            {t('Recibidas')} · {received.length}
          </p>
          {received.map((input) => {
            const { request, response } = requestEvents(detail, input);
            const files = fileCount(request, response);
            return (
              <article
                key={input.id}
                aria-label={`${t('Solicitud')} #${input.id}`}
                className='grid grid-cols-[12px_minmax(0,1fr)] items-start gap-3 px-2 py-1'
              >
                <Icon
                  icon={CheckIcon}
                  className='mt-1 text-success'
                  width={12}
                />
                <button
                  type='button'
                  aria-label={`${t('Ver detalle')} #${input.id}`}
                  className='block min-w-0 w-full rounded-small text-left text-xs leading-relaxed hover:bg-default-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus'
                  onClick={() => setOpenId(input.id)}
                >
                  <div className='flex flex-wrap items-baseline justify-between gap-2'>
                    <p className='font-semibold'>
                      {input.recipient}{' '}
                      <span className='ml-1 font-normal text-default-400'>
                        {t(
                          input.recipient_kind === 'external'
                            ? 'Externo'
                            : 'Interno'
                        )}
                      </span>
                    </p>
                    <span className='text-[11px] text-default-400'>
                      {t('Recibida')} {date(input.received_at!)}
                    </span>
                  </div>
                  <p className='mt-1 whitespace-pre-wrap break-words text-default-400'>
                    {input.requested}
                  </p>
                  {input.response && (
                    <p className='mt-1 whitespace-pre-wrap break-words'>
                      {input.response}
                    </p>
                  )}
                  <p className='mt-1 text-[11px] text-default-400'>
                    {input.recipient_kind === 'external' && (
                      <span>
                        {t('Seguimiento')}:{' '}
                        {input.followup_owner?.name || t('Sin asignar')}
                        {files > 0 ? ' · ' : ''}
                      </span>
                    )}
                    {files > 0 && (
                      <span className='inline-flex items-center gap-1'>
                        <Icon icon={PaperclipIcon} width={11} />
                        {documentsLabel(files)}
                      </span>
                    )}
                  </p>
                </button>
              </article>
            );
          })}
        </section>
      )}
      {open && openEvents && openTiming && (
        <Modal
          isOpen
          size='2xl'
          scrollBehavior='inside'
          onClose={() => setOpenId(null)}
        >
          <ModalContent>
            <ModalHeader className='flex flex-col gap-1'>
              <span>
                {t('Solicitud')} #{open.id} · {open.recipient}
              </span>
              <span className='flex flex-wrap items-center gap-2 text-xs font-normal text-default-500'>
                <StatusChip
                  tone={
                    open.received_at
                      ? 'success'
                      : openTiming.state === 'overdue' ||
                          openTiming.state === 'today'
                        ? 'warning'
                        : 'info'
                  }
                >
                  {open.received_at ? t('Recibida') : deadline(open)}
                </StatusChip>
                <span>
                  {t(
                    open.recipient_kind === 'external' ? 'Externo' : 'Interno'
                  )}
                  {open.external_entity
                    ? ` · ${open.external_entity.type}`
                    : ''}
                </span>
              </span>
            </ModalHeader>
            <ModalBody className='gap-5 pb-4'>
              <div className='flex flex-col gap-1'>
                <p className='text-[11px] uppercase tracking-wide text-default-400'>
                  {t('Qué se pidió')}
                </p>
                <p className='whitespace-pre-wrap break-words text-sm'>
                  {open.requested}
                </p>
                <p className='text-xs text-default-500'>
                  {t('Solicitado')} {date(open.requested_at, true)}
                  {openEvents.request?.actor_name
                    ? ` · ${openEvents.request.actor_name}`
                    : ''}{' '}
                  · {t('Vence el {{date}}', { date: dueDate(open.due_on) })}
                </p>
                {open.recipient_kind === 'external' && (
                  <p className='text-xs text-default-500'>
                    {t('Seguimiento')}:{' '}
                    {open.followup_owner?.name || t('Sin asignar')}
                  </p>
                )}
              </div>
              <div className='flex flex-col gap-1'>
                <p className='text-[11px] uppercase tracking-wide text-default-400'>
                  {t('Documentos de la solicitud')}
                </p>
                {openEvents.request && openEvents.request.files.length > 0 ? (
                  <CaseAttachments
                    identity={`${identity}:request:${open.id}`}
                    event={openEvents.request}
                    documents={detail.documents}
                  />
                ) : (
                  <p className='text-xs text-default-400'>
                    {t('Sin documentos')}
                  </p>
                )}
              </div>
              {open.received_at && (
                <div className='flex flex-col gap-1 border-t border-default-200 pt-4'>
                  <p className='text-[11px] uppercase tracking-wide text-default-400'>
                    {t('Respuesta')}
                  </p>
                  <p className='whitespace-pre-wrap break-words text-sm'>
                    {open.response || '—'}
                  </p>
                  <p className='text-xs text-default-500'>
                    {t('Recibida')} {date(open.received_at, true)}
                    {openEvents.response?.actor_name
                      ? ` · ${openEvents.response.actor_name}`
                      : ''}
                  </p>
                  <p className='mt-2 text-[11px] uppercase tracking-wide text-default-400'>
                    {t('Documentos de la respuesta')}
                  </p>
                  {openEvents.response &&
                  openEvents.response.files.length > 0 ? (
                    <CaseAttachments
                      identity={`${identity}:response:${open.id}`}
                      event={openEvents.response}
                      documents={detail.documents}
                    />
                  ) : (
                    <p className='text-xs text-default-400'>
                      {t('Sin documentos')}
                    </p>
                  )}
                </div>
              )}
            </ModalBody>
            <ModalFooter className='justify-between gap-2'>
              <Button variant='light' onPress={() => setOpenId(null)}>
                {t('Cerrar')}
              </Button>
              {!closed && !open.received_at && (
                <div className='flex flex-wrap justify-end gap-2'>
                  {open.recipient_kind === 'external' && (
                    <Button
                      variant='flat'
                      isDisabled={disabled}
                      onPress={() => {
                        setOpenId(null);
                        onAssign(open.id);
                      }}
                    >
                      {t('Editar encargado de seguimiento')}
                    </Button>
                  )}
                  <Button
                    color='primary'
                    isDisabled={disabled}
                    onPress={() => {
                      setOpenId(null);
                      onReply(open.id);
                    }}
                  >
                    {t('Registrar respuesta')}
                  </Button>
                </div>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </section>
  );
}
